import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { AdminDraft } from '../../_shared/domain/administrator-draft.model';
import { validateDraft } from '../../_shared/domain/administrator-draft.validation';
import { toAddAdministratorError } from '../domain/add-administrator-error';
import type { AddAdministratorGateway } from '../infrastructure/add-administrator.gateway';
import { submitRequested } from './add-administrator.actions';
import { selectDraft } from './add-administrator.selectors';
import { addAdministratorActions } from './add-administrator.slice';

/**
 * WORKER: send an admin invitation. Validation runs first (pure domain); on
 * failure it dispatches a `validation` business error and stops WITHOUT touching
 * the gateway. Otherwise all I/O goes through the injected gateway (read from the
 * saga `context`, never the api client), so tests drive every branch with an
 * in-memory gateway. `takeLatest` means a re-submit supersedes an in-flight one.
 */
function* saveWorker() {
  const draft = (yield select(selectDraft)) as AdminDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(addAdministratorActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: AddAdministratorGateway = yield getContext('addAdministratorGateway');
  yield put(addAdministratorActions.saveStarted());
  try {
    yield call(gateway.sendInvitation, draft);
    yield put(addAdministratorActions.saveSucceeded());
  } catch (e) {
    yield put(addAdministratorActions.saveFailed(toAddAdministratorError(e)));
  }
}

/** WATCHERS. */
export function* addAdministratorSaga() {
  yield all([takeLatest(submitRequested.type, saveWorker)]);
}
